import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useState, type ReactElement, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { IApiError } from '~/types/api'
import type { IAppUser, USER_TYPE } from '~/types/app-user'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import {
  keys as appsUsersKeys,
  useUpdateAppsUserMailMutation,
  useUpdateAppsUserPasswordMutation,
} from '~/queries/app-users'

import '../styles.scss'

interface Values {
  clientId?: string
  email?: string
  password?: string
}

const validationSchema = Yup.object().shape({
  clientId: Yup.string(),
  email: Yup.string().email(FORM_ERRORS.INVALID_EMAIL),
  password: Yup.string().matches(/^(?=.*[a-z])(?=.*[A-Z@#$%^&*.,!?;:"'\-—•…()[\]{}0-9@$%&*+=,]).{7,}|^$/, {
    message: 'At least 7 characters including one lowercase letter and one number, uppercase or special character',
  }),
})

interface ModalFormProps {
  env: USER_TYPE
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  selectedEmail: string
  user: IAppUser
}

const EditUserForm = ({ env, onClose, onFailure, onSuccess, selectedEmail, user }: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const defaultValues = {
    clientId: user.client_id,
    email: selectedEmail ?? '',
    password: '',
  }

  const [currentEmail, setCurrentEmail] = useState(selectedEmail)

  /** Queries & Mutations */

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setError,
  } = useForm<Values>({ defaultValues, resolver: yupResolver(validationSchema) })

  const { mutateAsync: updateAppsUserMail } = useUpdateAppsUserMailMutation()

  const { mutateAsync: updateAppsUserPassword } = useUpdateAppsUserPasswordMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    const { clientId, email, password } = values

    if (clientId && currentEmail && email && email !== currentEmail) {
      try {
        await updateAppsUserMail({
          clientId,
          currentEmail,
          env,
          newEmail: email,
          userId: user.user_id,
        })
        queryClient.invalidateQueries({ queryKey: appsUsersKeys.details(env, user.user_id) })
        onSuccess('Email successfully updated!')
      } catch (e) {
        const apiError = e as AxiosError<IApiError>

        switch (apiError.response?.status) {
          case 409:
            setError('email', { message: 'This email is the same' })
            break
          case 500:
            setError('email', { message: 'This email is already in use' })
            break
          default:
            onFailure('Email could not be updated')
        }
      }
    }

    if (password) {
      try {
        await updateAppsUserPassword({ env: env, password, user_id: user.user_id })
        onSuccess('Password successfully updated!')
      } catch (e) {
        onFailure('Password could not be updated')
      }
    }
  }

  useEffect(() => {
    if (selectedEmail) setCurrentEmail(selectedEmail)
  }, [selectedEmail])

  return (
    <>
      <form
        className="edit-apps-user-form modal-form"
        id="edit-apps-user-form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <TextInput
          error={errors.clientId?.message}
          label="Client id"
          placeholder="Enter a client id"
          type="text"
          {...register('clientId')}
        />
        <TextInput
          error={errors.email?.message}
          label="Email"
          placeholder={currentEmail ?? 'Enter an email address'}
          {...register('email')}
        />
        <TextInput
          error={errors.password?.message}
          label="Password"
          placeholder="Enter a new password"
          type="password"
          {...register('password')}
        />
      </form>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={!isDirty} form="edit-apps-user-form" type="submit">
          Save
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </>
  )
}

interface ModalProps {
  email: string
  env: USER_TYPE
  isOpen: boolean
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  user?: IAppUser
}

const EditUserModal = ({ email, env, isOpen, onClose, onFailure, onSuccess, user }: ModalProps): ReactElement => {
  return (
    <Modal className="edit-apps-user-modal apps-users-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Edit user</ModalHeader>
      {user ? (
        <EditUserForm
          env={env}
          onClose={onClose}
          onFailure={onFailure}
          onSuccess={onSuccess}
          selectedEmail={email}
          user={user}
        />
      ) : null}
    </Modal>
  )
}

export default EditUserModal
